import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
type Entry = { path: string; y: number; x: number };
const entries = new Map<number, Entry>();
/** Records only this app's actual history entries; never navigates outside the app. */
export function useNavigationTrail() {
  const location = useLocation();
  const restoreFrame = useRef(0);
  useLayoutEffect(() => {
    const index = window.history.state?.idx as number | undefined;
    if (index == null) return;
    const remembered = entries.get(index);
    const path = location.pathname + location.search;
    entries.set(
      index,
      remembered?.path === path
        ? remembered
        : { path, y: window.scrollY, x: window.scrollX },
    );
    const remember = () => {
      const current = entries.get(window.history.state?.idx);
      if (current) {
        current.x = window.scrollX;
        current.y = window.scrollY;
      }
    };
    window.addEventListener("scroll", remember, { passive: true });
    document.addEventListener("pointerdown", remember, true);
    if (remembered?.path === path) {
      const target = { ...remembered };
      let attempts = 0;
      const restore = () => {
        window.scrollTo(target.x, target.y);
        if (++attempts < 30 && Math.abs(window.scrollY - target.y) > 2)
          restoreFrame.current = requestAnimationFrame(restore);
      };
      restoreFrame.current = requestAnimationFrame(restore);
    }
    return () => {
      cancelAnimationFrame(restoreFrame.current);
      window.removeEventListener("scroll", remember);
      document.removeEventListener("pointerdown", remember, true);
    };
  }, [location.key, location.pathname, location.search]);
}
export function useReturnToPrevious(fallback: string) {
  const navigate = useNavigate();
  const location = useLocation();
  return () => {
    const current = window.history.state?.idx as number | undefined;
    if (current != null) {
      let index = current - 1;
      while (index >= 0) {
        const previous = entries.get(index);
        if (!previous) break;
        if (previous.path === location.pathname + location.search) {
          index--;
          continue;
        }
        if (
          !/^\/(login|set-password|forgot-password)(\?|$)/.test(previous.path)
        ) {
          navigate(index - current);
          return;
        }
        break;
      }
    }
    navigate(fallback, { replace: true, preventScrollReset: true });
  };
}
